import { ReactNode } from "react";

import { AiPart } from "@intelligems/ig-types/schemas/ai/ui-message-part";
import { PickType } from "@intelligems/ig-types/utils";
import { ToolStep } from "@intelligems/ig-types/schemas/ai/tool-progress";
import { ToolInput, ToolOutput } from "@src/components/ai-elements/tool";
import { getPrettyToolName } from "@src/features/ai/utils/tool-constants";

import { ToolSteps } from "./ToolSteps";

/**
 * A section inside an open tool card.
 *
 * Sections stack rather than compete: a call that reported steps, was given an
 * input and produced a result draws all three, in this order. That is why a
 * surface is handed the list rather than asked about one section at a time —
 * it can reorder them, merge them, or replace one and keep the rest.
 *
 * **The panel decides which sections apply**, and that stays true however the
 * content is drawn: whether the card opens at all, and whether a raw input or
 * result may be shown, are the panel's rules. A surface that re-derived them
 * would drift the first time they changed.
 */
export type ToolBlock =
  | { type: "steps"; steps: ToolStep[] }
  | { type: "input"; input: unknown }
  | { type: "output"; output: unknown; errorText?: string };

/** What a block needs besides its own data, to draw the way the panel would. */
export interface ToolBlockContext {
  part: PickType<AiPart, "dynamic-tool">;
  compact: boolean;
}

/**
 * Draws one section the way the panel draws it.
 *
 * Exported so a surface that only cares about one section hands the rest back
 * rather than reimplementing them:
 *
 * ```tsx
 * renderToolContent: (blocks, ctx) =>
 *   blocks.map((block) =>
 *     block.type === "steps"
 *       ? <MyOwnSteps key="steps" steps={block.steps} />
 *       : renderToolBlock(block, ctx)
 *   )
 * ```
 */
export const renderToolBlock = (
  block: ToolBlock,
  { part, compact }: ToolBlockContext
): ReactNode => {
  switch (block.type) {
    case "steps":
      return <ToolSteps key="steps" steps={block.steps} compact={compact} />;
    case "input":
      return (
        <ToolInput
          key="input"
          className={compact ? "space-y-1.5 px-2.5 py-2" : undefined}
          compact={compact}
          input={block.input}
        />
      );
    case "output":
      return (
        <ToolOutput
          key="output"
          className={compact ? "space-y-1.5 px-2.5 py-2" : undefined}
          compact={compact}
          errorText={block.errorText}
          imageDescriptionFallback={`Screenshot captured by ${getPrettyToolName(
            part.toolName
          )}`}
          output={block.output}
        />
      );
  }
};

/** The panel's own content: every applying section, in order. */
export const renderToolContent = (
  blocks: ToolBlock[],
  ctx: ToolBlockContext
): ReactNode => blocks.map((block) => renderToolBlock(block, ctx));
